import {
  type ChainSeamMemo,
  type StoryFrame,
  type StorySlice,
  chainSeamsFromMemo,
  distributeStory,
  recordChainSeams,
  storyFromChain,
} from '@/lib/text-linking/chain-story'

/**
 * Re-splits a chain's story against the seam memo: assembles the story across `storyFrames` from the
 * boundaries the memo recorded, distributes it over `targetFrames`, and records the resulting seams
 * back over `targetFrames`.
 *
 * `targetFrames` defaults to `storyFrames`. Frame-delete is the one caller that differs: its story is
 * assembled across the window INCLUDING the dying frames, so their boundaries are read like any
 * other, and distributed over the survivors, which is the shape the chain now has.
 *
 * Returns the FULL slice list. Callers diff it with `changedSlices` against whatever they treat as
 * "before" — the live frames, or the committed baselines.
 */
export function distributeChainStory(
  seamMemo: ChainSeamMemo,
  storyFrames: readonly StoryFrame[],
  targetFrames: readonly StoryFrame[] = storyFrames
): StorySlice[] {
  const story = storyFromChain(storyFrames, chainSeamsFromMemo(seamMemo, storyFrames))
  const { slices, seams } = distributeStory(story, targetFrames)

  recordChainSeams(seamMemo, targetFrames, slices, seams)

  return slices
}
